import { Badge } from '@/components/ui/Badge';
import { useMemo } from 'react';
import {
  AD_PRODUCT_METRIC_ROWS,
  EMPTY_RAW_METRICS,
  totalsFromMetricRows,
  WORKSPACE_METRIC_COLUMNS,
  type WorkspaceMetricsMaps,
} from '@/utils/workspaceAdProductMetrics';
import {
  WorkspaceMetricCell,
  WorkspaceMetricHeaderCells,
  WorkspaceMetricTotalsRow,
} from '@/components/workspace/WorkspaceMetricCell';
import { WorkspaceColumnHeader } from '@/components/workspace/WorkspaceColumnHeader';
import {
  workspaceColumnStickyProps,
  workspaceDraggedColumnClass,
  workspaceDropTargetClass,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import {
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  WorkspaceSection,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { WorkspaceTableSelectionActions } from '@/components/workspace/WorkspaceTableSelectionActions';
import type { WorkspaceDimensionSelectionProps } from '@/components/workspace/workspaceSelectionTypes';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { useColumnOrder, WORKSPACE_LABEL_LEADING_FREEZE_IDS } from '@/components/workspace/useColumnOrder';
import { useTableSort } from '@/components/workspace/useTableSort';
import {
  METRIC_ONLY_COLUMN_IDS,
  metricColumnPickerOptions,
} from '@/components/workspace/workspaceColumnConfig';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { exportMetricRollupCsv } from '@/api/workspaceCsvExport';
import { useToast } from '@/components/ui/Toast';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { rowMatchesMetricFilters } from '@/utils/workspaceMetricFilters';

export function formatWorkspaceRangeSubtitle(
  currentStart: string,
  currentEnd: string,
  previousStart: string,
  previousEnd: string,
  compareEnabled: boolean
): string {
  const fmt = (iso: string) =>
    new Date(iso + 'T12:00:00').toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    });

  const currentLabel =
    currentStart === currentEnd
      ? fmt(currentStart)
      : `${fmt(currentStart)} – ${fmt(currentEnd)}`;

  if (!compareEnabled) return currentLabel;

  const previousLabel =
    previousStart === previousEnd
      ? fmt(previousStart)
      : `${fmt(previousStart)} – ${fmt(previousEnd)}`;

  return `${currentLabel} vs ${previousLabel}`;
}

export function WorkspaceAdProductMetricsTable({
  current,
  previous,
  compareEnabled,
  isLoading,
  rangeSubtitle,
  isFiltered,
  selection,
}: {
  current: WorkspaceMetricsMaps;
  previous: WorkspaceMetricsMaps;
  compareEnabled: boolean;
  isLoading: boolean;
  rangeSubtitle: string;
  isFiltered: boolean;
  selection: WorkspaceDimensionSelectionProps;
}) {
  const { showToast } = useToast();
  const { range } = useAppDateRange();
  const exportStart = range.current.startDate;
  const exportEnd = range.current.endDate;
  const tableSort = useTableSort({ defaultSortBy: 'cost', defaultSortDir: 'desc' });
  const metricFilters = useMetricFilters({
    tableId: 'ad-product-metrics',
    metrics: WORKSPACE_METRIC_COLUMNS,
  });
  const columnOrder = useColumnOrder(METRIC_ONLY_COLUMN_IDS, undefined, WORKSPACE_LABEL_LEADING_FREEZE_IDS);
  const freezeLayout = useWorkspaceFrozenLayout(
    columnOrder.frozenColumnIds,
    columnOrder.displayOrder.join('\0')
  );
  const subtitleParts = [rangeSubtitle];
  if (isFiltered) subtitleParts.push('Filtered to selected entities');

  const rows = useMemo(
    () =>
      AD_PRODUCT_METRIC_ROWS.map(({ reportId, label }) => ({
        id: label,
        reportId,
        label,
        metrics: current.byAdProduct.get(reportId) ?? null,
        prevMetrics: previous.byAdProduct.get(reportId) ?? null,
      }))
        .filter((row) => rowMatchesMetricFilters(row.metrics, metricFilters.completeFilters))
        .sort((left, right) => {
          if (tableSort.sortBy === 'label') {
            const cmp = left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
            return tableSort.sortDir === 'asc' ? cmp : -cmp;
          }
          const cmp =
            ((left.metrics as Record<string, number> | null)?.[tableSort.sortBy] ?? 0) -
            ((right.metrics as Record<string, number> | null)?.[tableSort.sortBy] ?? 0);
          return tableSort.sortDir === 'asc' ? cmp : -cmp;
        }),
    [current, previous, metricFilters.completeFilters, tableSort.sortBy, tableSort.sortDir]
  );

  const rowIds = rows.map((row) => row.id);
  const allSelected =
    rowIds.length > 0 && rowIds.every((id) => selection.pendingIds.includes(id));

  const { currentTotals, previousTotals } = useMemo(() => {
    return {
      currentTotals: totalsFromMetricRows(rows.map((row) => row.metrics ?? EMPTY_RAW_METRICS)),
      previousTotals: totalsFromMetricRows(rows.map((row) => row.prevMetrics ?? EMPTY_RAW_METRICS)),
    };
  }, [rows]);

  return (
    <WorkspaceSection
      title="Performance by ad product"
      countLabel={subtitleParts.join(' · ')}
      actions={
        <>
          <WorkspaceMetricFiltersPopover metrics={WORKSPACE_METRIC_COLUMNS} {...metricFilters} />
          <WorkspaceTableSelectionActions
            entityLabel="ad product"
            selectionCount={selection.selectionCount}
            hasPendingChanges={selection.hasPendingChanges}
            canApply={selection.canApply}
            onApply={selection.onApply}
            onClearSelection={selection.onClearSelection}
          />
          <WorkspaceExportButton
            onExport={() => {
              exportMetricRollupCsv({
                filenamePrefix: 'workspace_ad_product_metrics',
                startDate: exportStart,
                endDate: exportEnd,
                labelHeader: 'Type',
                visibleMetricIds: columnOrder.visibleIds,
                metricColumns: WORKSPACE_METRIC_COLUMNS,
                rows,
              });
              showToast(`Exported ${rows.length} rows`, 'success');
            }}
          />
          <ReportColumnPicker
            columns={metricColumnPickerOptions()}
            selected={columnOrder.visibleIds}
            onChange={columnOrder.setVisibleIds}
            panelTitle="Visible columns"
          />
        </>
      }
    >
      <WorkspaceTableShell
        isEmpty={rows.length === 0}
        emptyMessage="Try adjusting metric filters, or clear them to see all ad products."
        isLoading={isLoading}
      >
        <table ref={freezeLayout.tableRef} className={workspaceTableClass.table}>
          <thead>
            <tr>
              <WorkspaceCheckboxCell isHeader>
                <SelectionCheckbox
                  checked={allSelected}
                  label="Select all ad products"
                  onChange={(checked) => selection.onToggleAll(rowIds, checked)}
                />
              </WorkspaceCheckboxCell>
              <WorkspaceColumnHeader
                label="Type"
                columnId="label"
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                movable={false}
                isFrozen={false}
                className={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById, 'header').className}
                style={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById, 'header').style}
              />
              <WorkspaceMetricHeaderCells
                columns={columnOrder.displayOrder
                  .map((columnId) => WORKSPACE_METRIC_COLUMNS.find((item) => item.key === columnId))
                  .filter((item): item is (typeof WORKSPACE_METRIC_COLUMNS)[number] => Boolean(item))}
                sortable
                movable
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                onFreeze={columnOrder.freezeColumn}
                frozenColumnIds={columnOrder.frozenColumnIds}
                pinnedFrozenColumnId={columnOrder.pinnedFrozenColumnId}
                frozenLeftById={freezeLayout.frozenLeftById}
                onDragStartColumn={columnOrder.startDrag}
                onDragHoverColumn={columnOrder.hoverReorder}
                onDropColumn={columnOrder.commitDrop}
                onDragEndColumn={columnOrder.endDrag}
                activeDragColumnId={columnOrder.activeDragColumnId}
                hoverColumnId={columnOrder.hoverColumnId}
                hoverPosition={columnOrder.hoverPosition}
                hoverInsertIndex={columnOrder.hoverInsertIndex}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(({ id, reportId, label, metrics, prevMetrics }) => {
              const selected = selection.pendingIds.includes(id);
              return (
                <tr key={reportId} className={tableRowClass(selected)}>
                  <WorkspaceCheckboxCell>
                    <SelectionCheckbox
                      checked={selected}
                      label={`Select ad product ${label}`}
                      onChange={(checked) => selection.onToggleId(id, checked)}
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceMiddleCell
                    className={`${workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById).className} ${columnOrder.activeDragColumnId === 'label' ? workspaceDraggedColumnClass(true) : ''}`}
                    style={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById).style}
                  >
                    <Badge variant="neutral" className="px-2.5 py-[3px] text-[11px] leading-[15px]">
                      {label}
                    </Badge>
                  </WorkspaceMiddleCell>
                  {columnOrder.displayOrder.map((columnId) => {
                    const def = WORKSPACE_METRIC_COLUMNS.find((item) => item.key === columnId);
                    if (!def) return null;
                    const isDragged = columnOrder.activeDragColumnId === columnId;
                    const isDropTarget =
                      columnOrder.hoverColumnId === columnId &&
                      columnOrder.activeDragColumnId !== columnId;
                    const sticky = workspaceColumnStickyProps(
                      columnId,
                      columnOrder.frozenColumnIds,
                      freezeLayout.frozenLeftById
                    );
                    return (
                    <WorkspaceMetricCell
                      key={def.key}
                      def={def}
                      current={(metrics as Record<string, number> | null)?.[def.key] ?? null}
                      previous={(prevMetrics as Record<string, number> | null)?.[def.key] ?? null}
                      compareEnabled={compareEnabled}
                      isLoading={isLoading}
                      className={`${sticky.className} ${workspaceDraggedColumnClass(isDragged)} ${workspaceDropTargetClass(isDropTarget, columnOrder.hoverPosition)}`}
                      style={sticky.style}
                    />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <WorkspaceMetricTotalsRow
              labelColSpan={2}
              frozenColumnIds={columnOrder.frozenColumnIds}
              frozenLeftById={freezeLayout.frozenLeftById}
              current={currentTotals}
              previous={previousTotals}
              compareEnabled={compareEnabled}
              isLoading={isLoading}
              columns={columnOrder.displayOrder
                .map((columnId) => WORKSPACE_METRIC_COLUMNS.find((item) => item.key === columnId))
                .filter((item): item is (typeof WORKSPACE_METRIC_COLUMNS)[number] => Boolean(item))}
            />
          </tfoot>
        </table>
      </WorkspaceTableShell>
    </WorkspaceSection>
  );
}
